/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect } from 'react'
import { Collapse } from 'react-bootstrap'
import { useServiceContext } from '../../../root'
import { InfiniteScrollSelect } from '../../../components/InfiniteScrollSelect'
import { getOptionsForAsyncPicker } from '../../../services/api/metadata'
import { useRcmTableData } from '../../../hooks/useRcmTableData'

const persistenceKey = 'settingsPropsFilter'

interface FilterFormValues {
  metadatas: any[]
}

const TableFilter: React.FC = () => {
  const context = useServiceContext()
  const { translate } = context.service.filter
  const tableData = useRcmTableData()

  const getDefaultFilter = (): FilterFormValues => ({
    metadatas: []
  })

  const [formValues, setFormValues] = useState<FilterFormValues>(getDefaultFilter())

  useEffect(() => {
    // Load persisted filter on mount
    const persistedFilter = context.service.persist.getRouteScoped<FilterFormValues>(persistenceKey)
    if (persistedFilter && persistedFilter.metadatas && persistedFilter.metadatas.length > 0) {
      setFormValues(persistedFilter)
      tableData.applyFilter(persistedFilter)
    }
  }, [])

  const applyFilter = (filter: FilterFormValues) => {
    context.service.persist.setRouteScoped(persistenceKey, filter)
    tableData.applyFilter(filter)
  }

  const handleApplyFilter = () => {
    applyFilter(formValues)
  }

  const handleClearFilter = () => {
    const defaultFilter = getDefaultFilter()
    setFormValues(defaultFilter)
    applyFilter(defaultFilter)
  }

  const isFilterDefault = (filter: FilterFormValues = formValues) => {
    const defaultFilter = getDefaultFilter()
    return JSON.stringify(filter) === JSON.stringify(defaultFilter)
  }

  const updateField = (field: keyof FilterFormValues, value: any) => {
    setFormValues((prev) => ({ ...prev, [field]: value }))
  }

  return (
    <Collapse in={!tableData.isFilterCollapsed}>
      <div>
        <div className="filter-wrapper">
          <div className="row">
            <div className="col-sm-6 col-lg-4">
              <div className="form-group" id="input-METADATA_FILTER_PROPS">
                <label className="control-label">{translate('PROPERTIES')}</label>
                <InfiniteScrollSelect
                  placeholder={translate('SELECT_PROPERTIES...')}
                  name="metadatas"
                  value={formValues.metadatas}
                  asyncLoadFn={async (search, page) => {
                    try {
                      return await getOptionsForAsyncPicker(search, page)
                    } catch (error) {
                      console.error('Error loading properties for filter:', error)
                      return {
                        options: [],
                        hasMore: false
                      }
                    }
                  }}
                  onChange={(option) => {
                    updateField('metadatas', option ? option : [])
                  }}
                  multi={true}
                  searchable
                  id="4e942414-ec58-4c21-8756-1922e1700dbb"
                />
              </div>
            </div>
          </div>
          <div>
            <button
              data-action="filter"
              className="btn btn-primary"
              onClick={handleApplyFilter}
              id="dc75f68d-1743-450e-9e6b-508ded49900f"
            >
              {translate('BTN_FILTER')}
            </button>
            <button
              data-action="reset"
              disabled={isFilterDefault()}
              className="btn btn-chita btn-link"
              onClick={handleClearFilter}
              id="31170f86-8f92-4eab-997f-21ade06f2cac"
            >
              {translate('BTN_CLEAR_FILTER')}
            </button>
          </div>
        </div>
      </div>
    </Collapse>
  )
}

export default TableFilter
